import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Clock, Flame, MapPin, Quote, Star, TrendingUp, Award, Heart, Eye, MessageCircle, Share2, Sparkles, Handshake, Building2, Users } from "lucide-react";
import { SiTiktok, SiInstagram, SiGoogle } from "react-icons/si";
import { Marquee } from "@/components/Marquee";
import { useEntry, useEntries, useField } from "@/lib/content";
import heroBirriaAsset from "@/assets/hero-birria.jpg.asset.json";
import heroBirriaMobileAsset from "@/assets/hero-birria-mobile.webp.asset.json";
const heroBirria = heroBirriaAsset.url;
const heroBirriaMobile = heroBirriaMobileAsset.url;
import grillFlames from "@/assets/grill-flames.jpg";
import community from "@/assets/community.jpg";
import tacoyaLogoAsset from "@/assets/tacoya-logo.png.asset.json";
const tacoyaLogo = tacoyaLogoAsset.url;


const SITE_URL = "https://tacoya.snapinsolutions.com";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Tacoya — The New Generation of Nordic Street Food" },
      { name: "description", content: "Born in Hietalahti. Proven in Helsinki. Ready to scale. Tacoya is one of Helsinki's most talked-about food brands — proven concept, media recognition and organic growth." },
      { property: "og:title", content: "Tacoya — Building a Nordic Street Food Brand" },
      { property: "og:description", content: "Proven concept. Two locations. Media recognition. Organic growth. Explore investment, franchise and partnership opportunities." },
      { property: "og:url", content: `${SITE_URL}/` },
      { property: "og:image", content: `${SITE_URL}/tacoya-logo.png` },
      { name: "twitter:image", content: `${SITE_URL}/tacoya-logo.png` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FoodEstablishment",
          name: "Tacoya",
          url: `${SITE_URL}/`,
          image: `${SITE_URL}/tacoya-logo.png`,
          description: "Helsinki-born birrieria and Nordic street food brand.",
          servesCuisine: ["Mexican", "Street Food", "Fusion"],
          priceRange: "€€",
          address: { "@type": "PostalAddress", addressLocality: "Helsinki", addressCountry: "FI" },
        }),
      },
    ],
  }),
  component: Index,
});

// Wrap react-icons so their svg has an accessible name AND is marked decorative via a parent span.
const brandIcon = (Cmp: React.ComponentType<any>, label: string) => {
  const Wrapped: React.FC<{ size?: number; className?: string }> = (props) => (
    <span aria-hidden="true" className="inline-flex">
      <Cmp {...props} title={label} aria-label={label} focusable="false" />
    </span>
  );
  Wrapped.displayName = `Brand(${Cmp.displayName || Cmp.name || "Icon"})`;
  return Wrapped;
};
const BrandGoogle = brandIcon(SiGoogle, "Google");
const BrandInstagram = brandIcon(SiInstagram, "Instagram");
const BrandTiktok = brandIcon(SiTiktok, "TikTok");

// Icon registry — maps content `icon` field values to components.
const ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  MapPin, Star, Eye, Users, Heart, MessageCircle, Share2, TrendingUp, Award, Flame, Sparkles, Building2, Handshake,
  Google: BrandGoogle,
  Instagram: BrandInstagram,
  TikTok: BrandTiktok,
  Tiktok: BrandTiktok,
};

function Index() {
  const f = useField();
  const marquee = useEntries("home", "marquee");
  const marqueeBuild = useEntries("home", "marquee_build");
  return (
    <>
      <Hero />
      <Marquee items={marquee.map((m) => f(m, "text"))} />
      <BornFromStreets />
      <MediaRecognition />
      <ByTheNumbers />
      <Birrieria />
      <BrandTimeline />
      <WhyTacoya />
      <BestSellers />
      <Marquee items={marqueeBuild.map((m) => f(m, "text"))} accent />
      <BuildWithUs />
      <CommunityWall />
      <KeyMessage />
      <CTA />
    </>
  );
}

function Hero() {
  const f = useField();
  const hero = useEntry("home", "hero");
  const heroImg = hero?.image ?? heroBirria;
  const heroMobileImg = hero?.image ? heroImg : heroBirriaMobile;
  return (
    <section id="order" className="relative min-h-[92vh] flex items-end overflow-hidden grain">
      <img
        src={heroImg}
        srcSet={`${heroMobileImg} 900w, ${heroImg} 1600w`}
        sizes="(max-width: 767px) 100vw, 100vw"
        alt="Birria taco dipped in consommé"
        width={1920}
        height={1280}
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-transparent to-transparent" />

      {/* Smoke plumes */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden mix-blend-screen">
        <span className="smoke-plume" style={{ left: "10%",  animationDelay: "0s",   ["--scale" as never]: "1"   }} />
        <span className="smoke-plume" style={{ left: "28%",  animationDelay: "-3s",  ["--scale" as never]: "1.3" }} />
        <span className="smoke-plume" style={{ left: "48%",  animationDelay: "-6s",  ["--scale" as never]: "1.1" }} />
        <span className="smoke-plume" style={{ left: "66%",  animationDelay: "-1.5s",["--scale" as never]: "1.4" }} />
        <span className="smoke-plume" style={{ left: "82%",  animationDelay: "-4.5s",["--scale" as never]: "1.2" }} />
      </div>

      {/* Fire glow bed at bottom */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-40 sm:h-56 mix-blend-screen">
        <div className="absolute inset-0 fire-glow" />
      </div>

      {/* Embers rising */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="ember"
            style={{
              left: `${(i * 53) % 100}%`,
              animationDelay: `${(i * 0.7) % 7}s`,
              animationDuration: `${5 + ((i * 1.3) % 5)}s`,
              ["--dx" as never]: `${((i % 5) - 2) * 14}px`,
            }}
          />
        ))}
      </div>


      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8 pt-20 sm:pt-24 md:pt-28 pb-12 sm:pb-16 md:pb-24 w-full">
        <h1 className="font-display text-[22vw] sm:text-[18vw] md:text-[10rem] leading-[0.85] text-cream">
          <span className="sr-only">Tacoya — The New Generation of Nordic Street Food</span>
          <span aria-hidden="true">{f(hero, "title_a", "TAC")}<span className="text-yellow">{f(hero, "title_o", "O")}</span>{f(hero, "title_b", "YA")}</span>
        </h1>
        <p className="mt-5 sm:mt-6 max-w-2xl font-display uppercase tracking-tight text-2xl sm:text-4xl md:text-5xl leading-[1.02] text-cream">
          {f(hero, "subtitle_a", "The New Generation of")} <span className="text-yellow">{f(hero, "subtitle_b", "Nordic Street Food")}</span>
        </p>
        <p className="mt-4 sm:mt-5 max-w-xl text-sm sm:text-base md:text-lg text-cream/90 font-bold tracking-wide uppercase">
          {f(hero, "tagline_a", "Born in Hietalahti.")} <span className="text-yellow">{f(hero, "tagline_b", "Proven in Helsinki.")}</span> {f(hero, "tagline_c", "Ready to Scale.")}
        </p>
        <p className="mt-3 max-w-lg text-sm sm:text-base text-cream/80 leading-relaxed">
          {f(hero, "blurb")}
        </p>
        <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-2.5 sm:gap-3">
          <a href={String(hero?.data.cta1_href ?? "#build")} className="group inline-flex items-center gap-2 bg-yellow text-charcoal px-5 sm:px-7 py-3 sm:py-4 text-[11px] sm:text-sm uppercase tracking-[0.18em] sm:tracking-widest font-bold rounded-full hover:bg-cream transition-colors whitespace-nowrap">
            {f(hero, "cta1_label", "Build With Us")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <Link to="/menu" className="inline-flex items-center gap-2 border border-cream/30 text-cream px-5 sm:px-7 py-3 sm:py-4 text-[11px] sm:text-sm uppercase tracking-[0.18em] sm:tracking-widest font-bold rounded-full hover:border-yellow hover:text-yellow transition-colors whitespace-nowrap">
            {f(hero, "cta2_label", "View Menu")}
          </Link>
          <Link to="/franchise" className="hidden sm:inline-flex items-center gap-2 bg-chili text-cream px-7 py-4 text-sm uppercase tracking-widest font-bold rounded-full hover:bg-cream hover:text-charcoal transition-colors whitespace-nowrap">
            {f(hero, "cta3_label", "Franchise")}
          </Link>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 md:right-10 hidden sm:flex flex-col items-end gap-1 text-cream/70 text-[10px] uppercase tracking-widest">
        <span>{f(hero, "badge_a", "Now operating")}</span>
        <span className="text-yellow font-bold">{f(hero, "badge_b", "2 Locations · Helsinki")}</span>
      </div>
    </section>
  );
}

function BornFromStreets() {
  const f = useField();
  const main = useEntry("home", "born_streets");
  const stats = useEntries("home", "born_stats");
  const img = main?.image ?? "";
  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      {/* Radial background wash */}
      <div aria-hidden className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_30%,rgba(69,10,10,0.55)_0%,transparent_55%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">

          {/* LEFT: Editorial photo frame */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="relative group flex-grow flex flex-col">
              {/* Viewfinder REC */}
              <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
                <span className="flex h-2 w-2 rounded-full bg-chili animate-pulse" />
                <span className="font-mono text-[10px] text-cream/80 tracking-widest uppercase">REC · 00:01:22:14</span>
              </div>
              <div className="absolute top-4 right-4 z-20 hidden sm:block">
                <span className="font-mono text-[10px] text-cream/70 tracking-tight uppercase">35mm · f1.8 · ISO 800</span>
              </div>

              {/* Image */}
              <div className="relative aspect-[4/5] w-full flex-grow overflow-hidden rounded-3xl border border-cream/15 bg-charcoal/60 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95),inset_0_1px_0_0_rgba(255,255,255,0.08)]">
                {img && (
                  <img
                    src={img}
                    alt="Tacoya kiosk, Hietalahti 2022"
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover brightness-75 transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                )}
                {/* Vignette + gloss */}
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-transparent to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-cream/[0.10] to-transparent" />
                <div className="pointer-events-none absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />

                {/* Rotating Tacoya seal */}
                <div className="absolute top-5 right-5 z-30 size-20 sm:size-24 rounded-full bg-cream/95 backdrop-blur-md shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7),inset_0_0_0_2px_rgba(0,0,0,0.06)] flex items-center justify-center animate-[spin_18s_linear_infinite]">
                  <img
                    src={f(main, "seal", tacoyaLogo)}
                    alt="Tacoya Birrieria seal"
                    className="size-[88%] object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]"
                  />
                </div>

                {/* Yellow badge */}
                <div className="absolute bottom-5 left-5 bg-yellow text-charcoal rounded-2xl px-5 sm:px-6 py-4 sm:py-5 flex flex-col items-start z-30 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.8)] border border-charcoal/10">
                  <span className="font-display text-4xl sm:text-5xl leading-none tracking-tight">{f(main, "badge_year", "2022")}</span>
                  <span className="mt-1.5 font-mono text-[9px] sm:text-[10px] font-bold tracking-[0.2em] uppercase">{f(main, "badge_place", "Hietalahti · HEL")}</span>
                </div>
              </div>


              {/* Caption strip */}
              <div className="mt-4 relative">
                <div className="h-px w-full bg-gradient-to-r from-yellow/40 via-cream/10 to-transparent" />
                <div className="mt-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="flex size-1.5 rounded-full bg-yellow shadow-[0_0_10px_rgba(250,204,21,0.8)]" />
                    <div className="flex flex-col leading-tight">
                      <span className="font-display text-[15px] text-yellow leading-none tracking-tight">{f(main, "caption_title", "Origin Story")}</span>
                      <span className="font-mono text-[10px] text-cream/75 tracking-normal mt-1">60.1644° N · 24.9293° E</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 py-1 rounded-sm border border-cream/15 bg-cream/[0.03]">
                    <span className="font-mono text-[9px] text-cream/70 uppercase tracking-[0.25em]">Archive</span>
                    <span className="font-mono text-[10px] text-cream font-bold tracking-wider">N°001</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Narrative + stats */}
          <div className="lg:col-span-7 flex flex-col justify-between py-2">
            <div className="space-y-8 md:space-y-10">
              {/* Kicker */}
              <div className="flex items-center gap-4">
                <div className="h-px w-12 bg-chili" />
                <span className="font-mono text-[11px] text-chili-ink font-bold tracking-[0.4em] uppercase">{f(main, "kicker", "Born From The Streets")}</span>
              </div>

              {/* Headline */}
              <h2 className="font-display text-yellow text-6xl sm:text-7xl md:text-7xl lg:text-8xl xl:text-[7.5rem] leading-[1.05] tracking-tight uppercase break-words">
                {f(main, "heading_a", "Built By The")} <br />{f(main, "heading_b", "Community.")}
              </h2>

              {/* Editorial copy block — Swiss grid */}
              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
                {/* Chapter 01 */}
                <div className="relative flex flex-col gap-5">
                  <div className="flex items-center justify-between border-t border-cream/10 pt-3">
                    <div className="flex items-center gap-3">
                      <span className="h-4 w-[2px] bg-chili" />
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-chili-ink">
                        {f(main, "chapter1_label", "Chapter 01")}
                      </span>
                    </div>
                    <span className="font-mono text-[10px] tracking-widest text-cream/70">
                      {f(main, "chapter1_date", "2022")}
                    </span>
                  </div>

                  <p className="text-cream/90 text-base sm:text-lg leading-snug font-medium">
                    {f(main, "chapter1_text")}
                  </p>
                </div>

                {/* Chapter 02 */}
                <div className="relative flex flex-col gap-5">
                  <div className="flex items-center justify-between border-t border-cream/10 pt-3">
                    <div className="flex items-center gap-3">
                      <span className="h-4 w-[2px] bg-chili" />
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-chili-ink">
                        {f(main, "chapter2_label", "Chapter 02")}
                      </span>
                    </div>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-cream/70">
                      {f(main, "chapter2_date", "Sep 2024")}
                    </span>
                  </div>

                  <blockquote className="relative pl-6 border-l border-yellow/30">
                    <Quote
                      size={22}
                      aria-hidden
                      className="absolute -left-[11px] -top-2 text-yellow/60 bg-charcoal px-0.5"
                      fill="currentColor"
                    />
                    <p className="text-cream text-lg sm:text-xl leading-relaxed font-sans font-light">
                      {f(main, "chapter2_text")}
                    </p>
                  </blockquote>
                </div>
              </div>

            </div>


            {/* Stats footer — glassmorphic */}
            <div
              role="list"
              className="mt-10 md:mt-16 relative grid grid-cols-1 sm:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-cream/15 backdrop-blur-2xl"
              style={{
                background:
                  "linear-gradient(135deg, color-mix(in oklab, white 8%, transparent) 0%, color-mix(in oklab, white 2%, transparent) 50%, color-mix(in oklab, white 5%, transparent) 100%)",
                boxShadow:
                  "inset 0 1px 0 0 rgba(255,255,255,0.18), inset 0 -1px 0 0 rgba(255,255,255,0.04)",
              }}
            >
              {/* Glossy top sheen */}
              <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cream/60 to-transparent z-10" />
              {/* Diagonal shine */}
              <span
                aria-hidden
                className="pointer-events-none absolute -top-1/2 left-0 w-2/3 h-[200%] rotate-[18deg] z-0 opacity-50"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)",
                }}
              />
              {stats.map((s, i) => (
                <div
                  key={s.key}
                  role="listitem"
                  className="relative group overflow-hidden transition-all p-5 sm:p-6 flex items-center gap-5 sm:block"
                  style={{
                    background:
                      "linear-gradient(180deg, color-mix(in oklab, white 4%, transparent), color-mix(in oklab, white 1%, transparent))",
                  }}
                >
                  {/* Subtle hover glow (no heavy shadow) */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 0%, color-mix(in oklab, var(--yellow) 14%, transparent), transparent 70%)",
                    }}
                  />
                  {/* Top hairline accent on hover */}
                  <span aria-hidden className="hidden sm:block absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-yellow/70 to-transparent scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-500" />
                  {/* Mobile left rail */}
                  <span aria-hidden className="absolute left-0 top-2 bottom-2 w-px sm:hidden bg-gradient-to-b from-transparent via-yellow/70 to-transparent" />

                  {/* Index chip */}
                  <span className="absolute top-3 right-3 font-mono text-[9px] text-cream/70 tracking-[0.2em] flex items-center gap-1.5">
                    <span className="h-px w-3 bg-cream/25" />0{i + 1}
                  </span>

                  {/* Value */}
                  <div className="relative shrink-0 sm:mb-3">
                    <div
                      className="font-display text-[2.75rem] sm:text-4xl md:text-5xl leading-[0.85] tracking-tight tabular-nums"
                      style={{
                        backgroundImage:
                          "linear-gradient(180deg, color-mix(in oklab, var(--yellow) 100%, white 25%) 0%, var(--yellow) 55%, color-mix(in oklab, var(--yellow) 85%, black 12%) 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {f(s, "value")}
                    </div>
                  </div>

                  {/* Label + sub */}
                  <div className="relative flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 sm:mb-1.5">
                      <span className="font-mono text-[10px] sm:text-[11px] text-cream font-bold uppercase tracking-[0.24em]">{f(s, "label")}</span>
                      <span className="h-px flex-1 bg-gradient-to-r from-chili/70 to-transparent sm:hidden" />
                    </div>
                    <span aria-hidden className="hidden sm:block h-px w-8 bg-chili/70 mb-2" />
                    <p className="text-[12px] sm:text-[11px] text-cream/70 leading-snug">{f(s, "sub")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

function Birrieria() {
  const f = useField();
  const main = useEntry("home", "birrieria");
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const cls = () => `pop-in${visible ? " in-view" : ""}`;
  return (
    <section className="relative py-16 md:py-28 overflow-hidden">
      <img src={main?.image ?? grillFlames} alt="" width={1600} height={1200} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/80 to-charcoal" />
      <div ref={ref} className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8 text-center">
        <div className={`text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-yellow mb-3 sm:mb-4 ${cls()}`} style={{ animationDelay: "0s" }}>{f(main, "kicker", "Pohjoismaiden ensimmäinen")}</div>
        <h2 className={`font-display text-[3.25rem] sm:text-6xl md:text-[10rem] leading-[0.85] text-cream ${cls()}`} style={{ animationDelay: "0.15s" }}>
          {f(main, "line1", "THE ONLY")}
        </h2>
        <h2 className={`font-display text-[3.25rem] sm:text-6xl md:text-[10rem] leading-[0.85] text-stroke-chili ${cls()}`} style={{ animationDelay: "0.55s" }}>
          {f(main, "line2", "BIRRIERIA")}
        </h2>
        <h2 className={`font-display text-[3.25rem] sm:text-6xl md:text-[10rem] leading-[0.85] text-yellow ${cls()}`} style={{ animationDelay: "0.95s" }}>
          {f(main, "line3", "IN FINLAND")}
        </h2>
        <p className="mt-6 sm:mt-8 max-w-2xl mx-auto text-cream/80 text-base sm:text-lg px-2">
          {f(main, "blurb")}
        </p>
        <div className="mt-16">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-12 bg-cream/30" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-cream/80">{f(main, "featured_label", "As featured in")}</span>
            <span className="h-px w-12 bg-cream/30" />
          </div>
          <PressCarousel />
        </div>
      </div>
    </section>
  );
}

const PRESS_ICONS: Record<string, React.ReactNode> = {
  hs: <span className="font-display text-sm leading-none tracking-tighter">HS</span>,
  city: <Star size={18} className="fill-yellow text-yellow" aria-hidden="true" />,
  google: <BrandGoogle size={16} />,
  tiktok: <BrandTiktok size={16} />,
  instagram: <BrandInstagram size={16} />,
};

function PressCarousel() {
  const f = useField();
  const entries = useEntries("home", "press_carousel");
  const press = entries.map((e) => ({
    outlet: f(e, "outlet"),
    date: f(e, "date"),
    kicker: f(e, "kicker"),
    quote: f(e, "quote"),
    icon: PRESS_ICONS[e.key] ?? <Star size={18} className="fill-yellow text-yellow" />,
    tint: String(e.data.tint ?? "var(--chili)"),
    featured: Boolean(e.data.featured),
  }));

  const [index, setIndex] = useState(2);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setAnimate(true);
      setIndex((i) => i + 1);
    }, 2800);
    return () => clearInterval(id);
  }, [paused]);

  useEffect(() => {
    if (press.length === 0 || index !== press.length + 1) return;
    const t = setTimeout(() => {
      setAnimate(false);
      setIndex(1);
    }, 1150);
    return () => clearTimeout(t);
  }, [index, press.length]);

  useEffect(() => {
    if (animate) return;
    const r = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(r);
  }, [animate]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const CARD_W = isMobile ? 240 : 320;
  const GAP = isMobile ? 14 : 24;
  const ACTIVE_SCALE = isMobile ? 1.08 : 1.18;
  const IDLE_SCALE = isMobile ? 0.78 : 0.82;

  if (press.length === 0) return null;

  const rendered = [press[press.length - 1], ...press, press[0]];
  const activeReal = ((index - 1) % press.length + press.length) % press.length;

  return (
    <div
      className="relative max-w-6xl mx-auto text-left"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative overflow-hidden py-8 sm:py-12 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
        <div
          className={
            animate
              ? "flex items-center transition-transform duration-[1100ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
              : "flex items-center"
          }
          style={{
            gap: `${GAP}px`,
            transform: `translateX(calc(50% - ${CARD_W / 2}px - ${index * (CARD_W + GAP)}px))`,
          }}
        >
          {rendered.map((p, i) => {
            const isActive = i === index;
            return (
              <div
                key={i}
                className={
                  animate
                    ? "shrink-0 transition-all duration-[1100ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
                    : "shrink-0"
                }
                style={{
                  width: `${CARD_W}px`,
                  transform: isActive ? `scale(${ACTIVE_SCALE})` : `scale(${IDLE_SCALE})`,
                  opacity: isActive ? 1 : 0.3,
                  filter: isActive ? "blur(0px)" : "blur(2px)",
                  zIndex: isActive ? 10 : 1,
                }}
              >
                <PressCard {...p} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots */}
      <div className="mt-6 flex items-center justify-center gap-2">
        {press.map((_, i) => (
          <button
            key={i}
            onClick={() => { setAnimate(true); setIndex(i + 1); }}
            aria-label={`Show press card ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === activeReal ? "w-8 bg-yellow" : "w-1.5 bg-cream/25 hover:bg-cream/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}


function PressCard({
  outlet, date, kicker, quote, icon, featured, tint = "var(--chili)", className = "",
}: {
  outlet: string; date: string; kicker: string; quote: string;
  icon: React.ReactNode; featured?: boolean; tint?: string; className?: string;
}) {
  return (
    <article
      className={`group relative bg-card border rounded-[2rem] ${featured ? "border-yellow shadow-[0_0_0_1px_var(--tacoya-yellow)]" : "border-border hover:border-cream/30"} px-5 sm:px-7 pt-10 sm:pt-12 pb-6 sm:pb-7 transition-all hover:-translate-y-1 overflow-hidden text-center flex flex-col items-center ${className}`}
    >
      {/* Ambient glow */}
      <div
        className="absolute -top-24 left-1/2 -translate-x-1/2 size-56 rounded-full opacity-25 blur-3xl group-hover:opacity-50 transition-opacity"
        style={{ background: tint }}
      />

      {featured && (
        <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-yellow text-charcoal px-2 py-1 font-bold z-10 rounded-full">
          Award · 2024
        </span>
      )}

      {/* Big circular logo */}
      <span
        className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0 mb-4"
        style={{
          background: `radial-gradient(circle at 30% 30%, color-mix(in oklab, ${tint} 35%, transparent), color-mix(in oklab, ${tint} 12%, transparent))`,
          color: tint,
          border: `1px solid color-mix(in oklab, ${tint} 45%, transparent)`,
          boxShadow: `0 8px 32px -8px color-mix(in oklab, ${tint} 60%, transparent), inset 0 1px 0 rgba(255,255,255,0.12)`,
        }}
      >
        <span className="[&>svg]:w-7 [&>svg]:h-7 sm:[&>svg]:w-8 sm:[&>svg]:h-8 flex items-center justify-center">
          {icon}
        </span>
      </span>

      {/* Outlet name */}
      <h3
        className="relative font-display text-3xl sm:text-4xl text-cream leading-none break-words tracking-tight uppercase"
        style={{
          backgroundImage: `linear-gradient(135deg, #fff 0%, color-mix(in oklab, ${tint} 60%, #fff) 60%, ${tint} 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          textShadow: `0 0 24px color-mix(in oklab, ${tint} 30%, transparent)`,
        }}
      >
        {outlet}
      </h3>

      {/* Kicker with flanking lines */}
      <div className="relative mt-3 flex items-center justify-center gap-2">
        <span
          aria-hidden
          className="h-px w-6 rounded-full"
          style={{ background: `linear-gradient(to right, transparent, ${tint})` }}
        />
        <span
          className="text-[10px] uppercase tracking-[0.28em] font-semibold whitespace-nowrap"
          style={{ color: tint }}
        >
          {kicker}
        </span>
        <span
          aria-hidden
          className="h-px w-6 rounded-full"
          style={{ background: `linear-gradient(to left, transparent, ${tint})` }}
        />
      </div>

      {/* Date / meta */}
      {!featured && (
        <span
          className={
            date.startsWith("@")
              ? "relative mt-2 font-sans text-[12px] sm:text-[13px] lowercase tracking-normal text-cream/70"
              : "relative mt-2 text-[9px] sm:text-[10px] uppercase tracking-widest text-cream/75"
          }
        >
          {date}
        </span>
      )}

      {/* Quote */}
      <div className="relative w-full mt-5 sm:mt-6">
        <div
          className="relative rounded-2xl border px-4 py-4 backdrop-blur-md overflow-hidden"
          style={{
            borderColor: `color-mix(in oklab, ${tint} 35%, transparent)`,
            background: `linear-gradient(135deg, color-mix(in oklab, ${tint} 10%, transparent), color-mix(in oklab, ${tint} 2%, transparent))`,
            boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.06), 0 8px 24px -16px color-mix(in oklab, ${tint} 60%, transparent)`,
          }}
        >
          <p className="relative text-[13px] sm:text-sm text-cream/90 leading-snug italic">
            <span className="font-display not-italic mr-0.5" style={{ color: tint }}>“</span>
            {quote}
            <span className="font-display not-italic ml-0.5" style={{ color: tint }}>”</span>
          </p>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-0 group-hover:w-2/3 transition-all duration-500"
        style={{ background: tint }}
      />
    </article>
  );
}

function MediaRecognition() {
  const f = useField();
  const main = useEntry("home", "media");
  const press = useEntries("home", "media_items");
  return (
    <section className="relative py-16 md:py-28 overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_80%_10%,rgba(214,35,42,0.18)_0%,transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px w-12 bg-chili" />
          <span className="font-mono text-[11px] text-chili-ink font-bold tracking-[0.4em] uppercase">{f(main, "kicker", "Media & Recognition")}</span>
        </div>
        <h2 className="font-display text-5xl sm:text-7xl md:text-8xl leading-[0.9] text-cream tracking-tight">
          {f(main, "heading_a", "FEATURED")} <span className="text-yellow">{f(main, "heading_b", "IN")}</span>
        </h2>
        <p className="mt-5 max-w-xl text-cream/70 text-base sm:text-lg leading-relaxed">
          {f(main, "blurb")}
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {press.map((p) => {
            const accent = String(p.data.accent ?? "var(--chili)");
            return (
              <article
                key={p.key}
                className="group relative overflow-hidden rounded-3xl border border-cream/12 bg-gradient-to-b from-card to-charcoal/60 p-6 sm:p-7 transition-all hover:-translate-y-1 hover:border-cream/30 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-20 right-0 size-44 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ background: accent }}
                />
                <div className="relative flex items-center justify-between gap-3">
                  <span className="font-display text-2xl sm:text-[1.6rem] leading-none tracking-tight text-cream uppercase">{f(p, "outlet")}</span>
                  <Award size={18} className="shrink-0 text-cream/30 group-hover:text-yellow transition-colors" />
                </div>
                <div className="relative mt-2 flex items-center gap-2">
                  <span className="h-px w-5 rounded-full" style={{ background: accent }} />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: accent }}>{f(p, "tag")}</span>
                </div>
                <p className="relative mt-5 text-cream/90 text-base sm:text-lg leading-snug font-medium">
                  <span className="font-display mr-1 align-top" style={{ color: accent }}>“</span>
                  {f(p, "headline")}
                  <span className="font-display ml-1 align-top" style={{ color: accent }}>”</span>
                </p>
                <span aria-hidden className="absolute bottom-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-500" style={{ background: accent }} />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ByTheNumbers() {
  const f = useField();
  const main = useEntry("home", "numbers");
  const stats = useEntries("home", "numbers_items");
  return (
    <section className="relative py-16 md:py-28 overflow-hidden bg-charcoal">
      <div aria-hidden className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_15%_20%,color-mix(in_oklab,var(--tacoya-yellow)_12%,transparent)_0%,transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px w-12 bg-yellow" />
          <span className="font-mono text-[11px] text-yellow font-bold tracking-[0.4em] uppercase">{f(main, "kicker", "Social Proof")}</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <h2 className="font-display text-5xl sm:text-7xl md:text-8xl leading-[0.9] text-cream tracking-tight">
            {f(main, "heading_a", "TACOYA BY")}<br /><span className="text-yellow">{f(main, "heading_b", "THE NUMBERS")}</span>
          </h2>
          <p className="max-w-md text-cream/70 text-base sm:text-lg leading-relaxed">
            {f(main, "blurb")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {stats.map((s) => {
            const Icon = ICONS[String(s.data.icon ?? "Star")] ?? Star;
            return (
              <div
                key={s.key}
                className="group relative overflow-hidden rounded-2xl border border-cream/10 bg-gradient-to-b from-card to-charcoal/70 p-5 flex flex-col transition-all hover:-translate-y-1 hover:border-yellow/60 shadow-[0_16px_40px_-26px_rgba(0,0,0,0.9)]"
              >
                <span aria-hidden className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 size-32 rounded-full bg-yellow/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="relative flex items-center justify-center size-9 rounded-xl bg-yellow/10 border border-yellow/25 text-yellow mb-4">
                  <Icon size={16} />
                </span>
                <div className="relative font-display text-3xl sm:text-4xl leading-none tracking-tight text-yellow tabular-nums">
                  {f(s, "value")}
                </div>
                <div className="relative mt-2 text-[13px] font-semibold text-cream leading-tight">{f(s, "label")}</div>
                <div className="relative mt-0.5 text-[11px] text-cream/75 leading-tight">{f(s, "sub")}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BrandTimeline() {
  const f = useField();
  const main = useEntry("home", "trajectory");
  const milestones = useEntries("home", "trajectory_items");
  return (
    <section className="relative py-16 md:py-28 mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-px w-12 bg-chili" />
        <span className="font-mono text-[11px] text-chili-ink font-bold tracking-[0.4em] uppercase">{f(main, "kicker", "The Trajectory")}</span>
      </div>
      <h2 className="font-display text-5xl sm:text-7xl md:text-8xl leading-[0.9] text-cream tracking-tight">
        {f(main, "heading_a", "FROM ONE LOCATION")}<br />{f(main, "heading_b", "TO A BRAND")}
      </h2>

      <div className="mt-14 relative">
        <div aria-hidden className="absolute left-[19px] sm:left-1/2 sm:-translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-yellow/60 via-cream/15 to-chili/60" />
        <div className="space-y-8 sm:space-y-0">
          {milestones.map((m, i) => {
            const future = Boolean(m.data.future);
            return (
              <div key={m.key} className={`relative sm:grid sm:grid-cols-2 sm:gap-12 sm:items-center ${i % 2 === 1 ? "sm:[&>*:first-child]:order-2" : ""}`}>
                <div className={`pl-12 sm:pl-0 ${i % 2 === 0 ? "sm:text-right sm:pr-12" : "sm:pl-12 sm:col-start-2"}`}>
                  <div className="py-4 sm:py-8">
                    <span className={`inline-block font-display text-4xl sm:text-5xl leading-none tracking-tight ${future ? "text-chili" : "text-yellow"}`}>{f(m, "year")}</span>
                    <p className="mt-2 text-cream/85 text-base sm:text-lg leading-snug max-w-sm sm:inline-block">{f(m, "text")}</p>
                  </div>
                </div>
                <span
                  aria-hidden
                  className={`absolute left-[13px] sm:left-1/2 sm:-translate-x-1/2 top-6 sm:top-1/2 sm:-translate-y-1/2 size-3.5 rounded-full border-2 border-charcoal ${future ? "bg-chili shadow-[0_0_14px_var(--chili)]" : "bg-yellow shadow-[0_0_14px_var(--tacoya-yellow)]"}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyTacoya() {
  const f = useField();
  const main = useEntry("home", "edge");
  const reasons = useEntries("home", "edge_items");
  return (
    <section className="relative py-16 md:py-28 bg-cream text-charcoal overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-12 bg-chili" />
            <span className="font-mono text-[11px] text-chili-ink font-bold tracking-[0.4em] uppercase">{f(main, "kicker", "The Edge")}</span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl leading-[0.9] tracking-tight">
            {f(main, "heading_a", "WHY")} <span className="text-chili">{f(main, "heading_b", "TACOYA")}</span>
          </h2>
          <p className="mt-5 text-charcoal/70 text-base sm:text-lg leading-relaxed">
            {f(main, "blurb")}
          </p>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3 md:gap-4">
          {reasons.map((r, i) => {
            const Icon = ICONS[String(r.data.icon ?? "Flame")] ?? Flame;
            return (
              <div
                key={r.key}
                className="group relative flex items-center gap-4 rounded-2xl border border-charcoal/10 bg-white/60 px-5 py-5 transition-all hover:-translate-y-0.5 hover:border-charcoal/30 hover:bg-white"
              >
                <span className="flex items-center justify-center size-11 rounded-xl bg-charcoal text-yellow shrink-0">
                  <Icon size={18} />
                </span>
                <span className="font-semibold text-charcoal text-base sm:text-lg leading-tight">{f(r, "text")}</span>
                <span className="ml-auto font-mono text-[11px] text-charcoal/70 tracking-widest">0{i + 1}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuildWithUs() {
  const f = useField();
  const main = useEntry("home", "build");
  const tracks = useEntries("home", "build_items");
  return (
    <section id="build" className="relative py-16 md:py-28 mx-auto max-w-7xl px-4 sm:px-5 md:px-8 scroll-mt-24">
      <div className="relative overflow-hidden rounded-[2rem] border border-yellow/30 bg-gradient-to-b from-card to-charcoal p-6 sm:p-10 md:p-14 grain">
        <div aria-hidden className="absolute -top-32 -right-24 size-96 rounded-full bg-yellow/20 blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute -bottom-32 -left-24 size-80 rounded-full bg-chili/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-12 bg-yellow" />
            <span className="font-mono text-[11px] text-yellow font-bold tracking-[0.4em] uppercase">{f(main, "kicker", "Investor & Partnership")}</span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl md:text-8xl leading-[0.9] text-cream tracking-tight">
            {f(main, "heading_a", "BUILD")} <span className="text-yellow">{f(main, "heading_b", "WITH US")}</span>
          </h2>
          <p className="mt-5 max-w-2xl text-cream/80 text-base sm:text-lg leading-relaxed">
            {f(main, "blurb")}
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {tracks.map((t) => {
              const Icon = ICONS[String(t.data.icon ?? "Building2")] ?? Building2;
              return (
                <div
                  key={t.key}
                  className="group relative overflow-hidden rounded-2xl border border-cream/12 bg-cream/[0.03] backdrop-blur-sm p-6 transition-all hover:-translate-y-1 hover:border-yellow/50"
                >
                  <span className="flex items-center justify-center size-12 rounded-xl bg-yellow/10 border border-yellow/25 text-yellow mb-5">
                    <Icon size={20} />
                  </span>
                  <h3 className="font-display text-2xl text-cream leading-none tracking-wide uppercase">{f(t, "title")}</h3>
                  <p className="mt-3 text-sm text-cream/80 leading-relaxed">{f(t, "desc")}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={String(main?.data.cta1_href ?? "mailto:hello@tacoya.fi")}
              className="group inline-flex items-center gap-2 bg-yellow text-charcoal px-7 py-4 text-xs sm:text-sm uppercase tracking-widest font-bold rounded-full hover:bg-cream transition-colors"
            >
              {f(main, "cta1_label", "Contact Us")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/franchise"
              className="inline-flex items-center gap-2 border border-cream/30 text-cream px-7 py-4 text-xs sm:text-sm uppercase tracking-widest font-bold rounded-full hover:border-yellow hover:text-yellow transition-colors"
            >
              {f(main, "cta2_label", "Franchise Details")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function KeyMessage() {
  const f = useField();
  const main = useEntry("home", "key_message");
  const lines = useEntries("home", "key_message_lines");
  return (
    <section className="relative py-20 md:py-32 mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
      <div className="max-w-4xl">
        <div className="space-y-5 sm:space-y-7">
          {lines.map((l) => (
            <p key={l.key} className="font-display text-3xl sm:text-5xl md:text-6xl leading-[1] tracking-tight text-cream/70">
              {f(l, "a")} <span className="text-cream">{f(l, "b")}</span>
            </p>
          ))}
        </div>
        <p className="mt-10 sm:mt-14 font-display text-4xl sm:text-6xl md:text-7xl leading-[1] tracking-tight text-yellow">
          {f(main, "final", "Tacoya is building a brand.")}
        </p>
      </div>
    </section>
  );
}

function BestSellers() {
  const f = useField();
  const heading = useEntry("home", "best_sellers", "heading");
  const items = useEntries("home", "best_sellers_items").filter((i) => i.key !== "heading");
  return (
    <section id="bestsellers" className="py-16 md:py-28 mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-none text-cream">
          {f(heading, "heading_a", "BEST")} <span className="text-chili">{f(heading, "heading_b", "SELLERS")}</span>
        </h2>
        <Link
          to="/menu"
          className="group relative inline-flex items-center gap-3 rounded-full border border-yellow/40 bg-gradient-to-r from-yellow/10 via-yellow/5 to-transparent pl-5 pr-2 py-2 text-cream hover:border-yellow hover:bg-yellow/15 transition-all shadow-[0_8px_24px_-12px_color-mix(in_oklab,var(--tacoya-yellow)_60%,transparent)] overflow-hidden"
        >
          <span className="absolute inset-y-0 left-0 w-0 group-hover:w-full bg-yellow/10 transition-all duration-500 ease-out -z-0" />
          <span className="relative text-[11px] sm:text-xs uppercase tracking-[0.25em] font-semibold">{f(heading, "link_label", "Full menu")}</span>
          <span className="relative flex items-center justify-center size-8 rounded-full bg-yellow text-charcoal font-bold transition-transform group-hover:translate-x-0.5 group-hover:rotate-[-8deg]">
            →
          </span>
        </Link>

      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {items.map((item) => {
          const name = f(item, "name");
          const tag = f(item, "tag");
          const spice = Number(item.data.spice ?? 0);
          return (
            <article
              key={item.key}
              className="group relative overflow-hidden rounded-3xl bg-gradient-to-b from-[#26201a] via-card to-[#15110d] border border-cream/10 hover:border-yellow/60 transition-all duration-500 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.9),inset_0_1px_0_0_rgba(255,255,255,0.08)] hover:-translate-y-1 hover:shadow-[0_30px_70px_-25px_color-mix(in_oklab,var(--tacoya-yellow)_35%,rgba(0,0,0,0.9))]"
            >
              {/* glossy top sheen */}
              <span className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-cream/[0.10] to-transparent z-20" />
              {/* specular hairline */}
              <span className="pointer-events-none absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent z-20" />

              <div className="relative aspect-[4/5] overflow-hidden rounded-t-3xl">
                {item.image && <img src={item.image} alt={name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out" />}
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/30 to-transparent" />

                {tag && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-yellow text-charcoal text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-bold shadow-[0_4px_12px_-2px_rgba(0,0,0,0.5)]">
                    <span className="size-1.5 rounded-full bg-charcoal animate-pulse" />
                    {tag}
                  </div>
                )}
                <div className="absolute top-3 right-3 flex gap-0.5 rounded-full bg-charcoal/85 backdrop-blur-md border border-cream/15 px-2.5 py-1.5">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <Flame key={idx} size={11} className={idx < spice ? "text-chili fill-chili drop-shadow-[0_0_4px_var(--chili)]" : "text-cream/25"} />
                  ))}
                </div>
              </div>

              <div className="relative p-6 pt-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-yellow/80 mb-2 font-semibold">
                  Signature · Tacoya
                </div>
                <h3 className="font-sans font-semibold text-3xl md:text-[2rem] leading-tight tracking-tight text-cream group-hover:text-yellow transition-colors">
                  {name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/70 border-l-2 border-chili/70 pl-3">
                  {f(item, "desc")}
                </p>
              </div>
            </article>
          );
        })}
      </div>

    </section>
  );
}


// Asymmetric magazine grid (12-col bento) — positional spans for community tiles.
const TILE_SPANS = [
  "aspect-[4/5] md:col-span-7 md:row-span-2 md:aspect-auto md:h-[560px]",
  "aspect-[4/5] md:col-span-5 md:aspect-auto md:h-[270px]",
  "aspect-[4/5] md:col-span-5 md:aspect-auto md:h-[270px]",
  "aspect-[4/5] md:col-span-4 md:aspect-square",
  "aspect-[4/5] md:col-span-4 md:aspect-square",
  "aspect-[4/5] md:col-span-4 md:aspect-square",
  "aspect-[4/5] md:col-span-6 md:aspect-auto md:h-[300px]",
  "aspect-[4/5] md:col-span-6 md:aspect-auto md:h-[300px]",
  "aspect-[4/5] md:col-span-5 md:row-span-2 md:aspect-auto md:h-[560px]",
  "aspect-[4/5] md:col-span-7 md:aspect-auto md:h-[270px]",
  "aspect-[4/5] md:col-span-7 md:aspect-auto md:h-[270px]",
  "aspect-[4/5] md:col-span-4 md:aspect-square",
  "aspect-[4/5] md:col-span-4 md:aspect-square",
  "aspect-[4/5] md:col-span-4 md:aspect-square",
  "aspect-[4/5] md:col-span-6 md:aspect-auto md:h-[300px]",
  "aspect-[4/5] md:col-span-6 md:aspect-auto md:h-[300px]",
  "aspect-[4/5] md:col-span-12 md:aspect-auto md:h-[320px]",
];

function CommunityWall() {
  const f = useField();
  const main = useEntry("home", "community");
  const tiles = useEntries("home", "community_tiles");
  const hashtag = f(main, "hashtag", "#tacoyahelsinki");
  const hashtagUrl = String(main?.data.hashtag_url ?? "https://www.instagram.com/tacoya/");

  return (
    <section className="py-16 md:py-28 mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-chili" />
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-chili-ink font-bold">{f(main, "kicker", "Community · Vol.01")}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.9] text-cream tracking-tight">
            {f(main, "heading_a", "THIS IS A")} <span className="text-yellow">{f(main, "heading_b", "MOVEMENT.")}</span>
          </h2>
          <p className="mt-3 text-cream/75 text-sm sm:text-base max-w-md">
            {f(main, "blurb")}
          </p>
        </div>
        <a
          href={hashtagUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-cream/70 hover:text-yellow transition-colors"
        >
          <span className="size-1.5 rounded-full bg-yellow animate-pulse" />
          {hashtag}
          <span className="text-yellow group-hover:translate-x-0.5 transition-transform">↗</span>
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-auto gap-3 md:gap-4">
        {tiles.map((t, i) => (
          <figure
            key={t.key}
            className={`group relative overflow-hidden rounded-2xl md:rounded-[1.5rem] ring-1 ring-cream/10 hover:ring-yellow/40 transition-all bg-charcoal ${TILE_SPANS[i % TILE_SPANS.length]}`}
          >
            {t.image && (
              <img
                src={t.image}
                alt={f(t, "caption")}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              />
            )}

            {/* Top sheen */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cream/30 to-transparent" />

            {/* Corner accent */}
            <span aria-hidden className="absolute bottom-0 left-0 h-0.5 w-0 group-hover:w-full bg-gradient-to-r from-yellow to-chili transition-all duration-700" />
          </figure>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  const f = useField();
  const main = useEntry("home", "cta");
  const ctaImg = main?.image ?? community;
  const addressUrl = String(main?.data.address_url ?? "https://maps.google.com/?q=Lapinlahdenkatu+Helsinki");
  const reviewsUrl = String(main?.data.reviews_url ?? "https://share.google/zDx6qgDwIgmX4NdFx");
  return (
    <section className="py-20 md:py-32 mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
      <div className="relative rounded-3xl overflow-hidden border border-chili/40 shadow-[0_30px_80px_-30px_rgba(214,35,42,0.5)] grid md:grid-cols-12 bg-chili">
        {/* Image side */}
        <div className="relative md:col-span-5 min-h-[260px] md:min-h-[420px] lg:min-h-[460px] overflow-hidden">
          <img src={ctaImg} alt="Tacoya Kamppi" className="absolute inset-0 size-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-chili/40 via-chili/10 to-chili/80 md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-chili" />
          {/* Floating info chip */}
          <div className="absolute top-5 left-5 bg-charcoal/80 backdrop-blur-md rounded-full px-3 py-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-cream">
            <span className="size-1.5 rounded-full bg-yellow animate-pulse" />
            Open Now · Until 22:00
          </div>
          <a
            href={reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Tacoya Birrieria on Google Reviews"
            className="group absolute bottom-5 left-5 flex items-center gap-2.5 bg-white text-[#3c4043] rounded-full pl-2 pr-3 py-1.5 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] ring-1 ring-black/5 hover:shadow-[0_14px_36px_-10px_rgba(0,0,0,0.7)] hover:-translate-y-0.5 transition-all"
          >
            {/* Google G logo */}
            <span className="flex size-5 items-center justify-center rounded-full bg-white">
              <svg viewBox="0 0 48 48" className="size-4" aria-hidden>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            </span>
            <span className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold tracking-tight">
              <span className="font-bold text-[#1a73e8]">{f(main, "rating", "4.6")}</span>
              <span className="flex" aria-hidden>
                {[0, 1, 2, 3].map((i) => (
                  <Star key={i} size={11} className="fill-[#fbbc05] text-[#fbbc05]" />
                ))}
                <span className="relative inline-block">
                  <Star size={11} className="text-[#e0e0e0]" />
                  <span className="absolute inset-0 overflow-hidden" style={{ width: "60%" }}>
                    <Star size={11} className="fill-[#fbbc05] text-[#fbbc05]" />
                  </span>
                </span>
              </span>
              <span className="text-[#5f6368]">{f(main, "reviews_label", "· 275 Google reviews")}</span>
            </span>
          </a>
        </div>

        {/* Content side */}
        <div className="relative md:col-span-7 p-5 sm:p-10 md:p-10 lg:p-14 text-cream grain">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-yellow/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-charcoal/30 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-yellow mb-3 sm:mb-4 flex items-center gap-2">
              <MapPin size={14} /> {f(main, "kicker", "Helsinki · Kamppi Flagship")}
            </div>
            <h2 className="font-display text-[3.5rem] sm:text-6xl md:text-7xl xl:text-8xl leading-[1.05]">
              {f(main, "heading_a", "COME EAT")}<br />{f(main, "heading_b", "WITH US.")}
            </h2>
            <p className="mt-5 sm:mt-6 text-cream/90 text-sm sm:text-lg leading-relaxed max-w-xl">
              {f(main, "blurb")}
            </p>

            {/* Info rows */}
            <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
              <a
                href={addressUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex items-center gap-3.5 rounded-2xl px-4 py-3.5 border border-cream/15 bg-gradient-to-br from-charcoal/60 via-charcoal/40 to-charcoal/20 backdrop-blur-md overflow-hidden transition-all duration-300 hover:border-yellow/50 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-20px_rgba(250,204,21,0.5)]"
              >
                <span aria-hidden className="pointer-events-none absolute -top-10 -right-10 size-24 rounded-full bg-yellow/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[2px] bg-gradient-to-b from-transparent via-yellow to-transparent opacity-60" />
                <span className="relative flex items-center justify-center size-10 rounded-xl bg-gradient-to-br from-yellow/30 to-yellow/5 border border-yellow/30 text-yellow shrink-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]">
                  <MapPin size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-cream/75 font-mono">
                    <span className="size-1 rounded-full bg-yellow animate-pulse" />
                    {f(main, "address_label", "Address")}
                  </div>
                  <div className="text-sm font-semibold text-cream truncate mt-0.5">{f(main, "address", "Lapinlahdenkatu")}</div>
                </div>
                <ArrowRight size={14} className="text-cream/70 group-hover:text-yellow group-hover:translate-x-1 transition-all shrink-0" />
              </a>
              <div className="group relative flex items-center gap-3.5 rounded-2xl px-4 py-3.5 border border-cream/15 bg-gradient-to-br from-charcoal/60 via-charcoal/40 to-charcoal/20 backdrop-blur-md overflow-hidden transition-all duration-300 hover:border-yellow/50">
                <span aria-hidden className="pointer-events-none absolute -top-10 -right-10 size-24 rounded-full bg-yellow/15 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[2px] bg-gradient-to-b from-transparent via-yellow to-transparent opacity-60" />
                <span className="relative flex items-center justify-center size-10 rounded-xl bg-gradient-to-br from-yellow/30 to-yellow/5 border border-yellow/30 text-yellow shrink-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]">
                  <Clock size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-cream/75 font-mono">
                    <span className="size-1 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                    {f(main, "hours_label", "Open Now")}
                  </div>
                  <div className="text-sm font-semibold text-cream truncate mt-0.5">{f(main, "hours", "Mon–Sat 11–22 · Sun 12–21")}</div>
                </div>
              </div>
            </div>


            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/locations" className="group inline-flex items-center gap-2 bg-yellow text-charcoal px-6 py-3.5 text-xs sm:text-sm uppercase tracking-widest font-bold rounded-full hover:bg-cream transition-colors">
                {f(main, "cta1_label", "Find Us")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/franchise" className="inline-flex items-center gap-2 border border-cream/40 text-cream px-6 py-3.5 text-xs sm:text-sm uppercase tracking-widest font-bold rounded-full hover:bg-cream hover:text-charcoal transition-colors">
                {f(main, "cta2_label", "Franchise")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
